/**
 * THE SUGGESTION LIST — the rows that open above the composer while a command is being typed.
 *
 * A TEMPLATE, NOT AN ELEMENT, because of where the list has to live for a screen reader. The text
 * box points at the active row with `aria-activedescendant`, and that ID reference only resolves
 * inside one shadow root. A list inside its own custom element would put the rows in a second root,
 * and the reference would point at nothing. So the composer renders this into its own root, the
 * same way it renders the attachment chips.
 *
 * Why not the search bar's autocomplete: it only understands `/name=value`, draws into the page
 * body with global styles, and is built for a one-line input.
 */
import { css, html, nothing } from 'lit';
/** The ID of the active row, for the text box's `aria-activedescendant`. */
export const activeRowId = (v) => `${v.id}-${v.active}`;
export function renderSuggestList(v) {
    if (!v.items.length)
        return nothing;
    // mousedown, not click: a click would take focus from the text box first, and the person is
    // still typing in it.
    return html `<div class="suggest" role="listbox" id=${v.id} aria-label=${v.label}>
    ${v.items.map((s, i) => html `<div
        class="opt ${i === v.active ? 'on' : ''}"
        role="option"
        id="${v.id}-${i}"
        aria-selected=${i === v.active ? 'true' : 'false'}
        @mousedown=${(e) => {
        e.preventDefault();
        v.onPick(i);
    }}
        @mousemove=${() => {
        if (i !== v.active)
            v.onHover(i);
    }}
      >
        <span class="lab ${s.label.startsWith('/') ? 'mono' : ''}">${s.label}${s.hint ? html ` <span class="hint">${s.hint}</span>` : nothing}</span>
        ${s.detail ? html `<span class="det">${s.detail}</span>` : nothing}
      </div>`)}
  </div>`;
}
/** Positioned against the nearest positioned ancestor, which the host makes the box's wrapper: the
 *  list opens upward over the transcript, never over the text being typed. */
export const suggestStyles = css `
  .suggest {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% + 6px);
    z-index: 5;
    max-height: 264px;
    overflow-y: auto;
    padding: 4px;
    border: 1px solid var(--border-color);
    border-radius: 10px;
    background: var(--card-bg);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.42);
  }
  .suggest .opt {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
  }
  .suggest .opt.on {
    background: color-mix(in srgb, var(--ai) 14%, transparent);
  }
  .suggest .lab {
    flex-shrink: 0;
    color: var(--text-primary);
  }
  /* Command names are typed exactly as shown, so they read as code; model and chapter names do not. */
  .suggest .lab.mono {
    font-family: var(--font-mono, ui-monospace, monospace);
  }
  .suggest .hint {
    color: var(--text-tertiary);
  }
  .suggest .det {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-secondary);
  }
`;
//# sourceMappingURL=suggest-list.js.map